import { find, mapValues, map } from 'lodash';
import moment from 'moment';
import { Column } from 'react-base-table';

import { humanize } from '@utils';
import { FONT_FAMILY } from '@utils/constants';
import { CommonHeaderRenderer } from './MetricsTable/UsageBaseTable/headerRenderers';
import { CommonCellRenderer } from '@components/usage-dashboard/MetricsTable/UsageBaseTable/cellRenderers';
import { usageTableMetricList } from '@components/usage-dashboard/atoms';
import { TOTAL_BILLABLE_HEADER_DISPLAY_NAME } from '@components/usage-dashboard/constant';
import { getUsageMetricsMeta } from '@components/usage-dashboard/hooks/utils';

const axisColor = '#ccc';
const axisLabelColor = '#000';
export const TIME_AXIS = 'time-axis';
export const VALUE_AXIS = 'value-axis';
export const DOD_TIME_AXIS = 'dod-time-axis';
const COLUMN_NAME_WIDTH = 300;
const COLUMN_DEFAULT_WIDTH = 150;

export const utcMoment = moment.utc;

const PRIMARY_SERIES = [
  {
    id: 'USAGE_METRIC_SERIES',
    type: 'line',
  },
];

const timeXAxis = {
  id: TIME_AXIS,
  type: 'time',
  show: true,
  splitNumber: 10,
  axisLabel: {
    color: axisLabelColor,
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
  },
  splitLine: {
    show: false,
  },
  axisTick: {
    alignWithLabel: false,
    lineStyle: {
      color: axisColor,
    },
  },
  axisLine: {
    show: true,
    lineStyle: {
      color: axisColor,
    },
  },
};
const valueYAxis = {
  id: VALUE_AXIS,
  type: 'value',
  show: true,
  splitNumber: 2,
  nameLocation: 'middle',
  axisLabel: {
    color: axisLabelColor,
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
  },
  splitLine: {
    show: true,
  },
  axisTick: {
    show: false,
  },
  axisLine: {
    show: false,
    lineStyle: {
      color: axisColor,
    },
  },
};
export const chartOption = (metric) => {
  return {
    id: metric,
    xAxis: [timeXAxis],
    yAxis: [valueYAxis],
    grid: {
      top: 20,
      bottom: 35,
      left: 50,
      right: 35,
    },
    series: [...PRIMARY_SERIES],
    tooltip: {
      trigger: 'axis',
      textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
      confine: true,
      appendToBody: true,
      className: 'echarts-tooltip',
    },
    dataZoom: [
      {
        type: 'slider',
        show: false,
        xAxisIndex: [0],
      },
      {
        type: 'slider',
        show: false,
        xAxisIndex: 1,
      },
    ],
    toolbox: {
      show: false,
    },
    brush: {
      brushType: 'lineX',
      seriesIndex: [0],
      brushLink: [0],
      xAxisIndex: [0],
      transformable: false,
      throttleType: 'debounce',
      throttleDelay: 300,
      brushStyle: {
        borderWidth: 1,
        color: 'rgba(120,140,180,0.3)',
        borderColor: 'rgb(224, 224, 224,0.8)',
      },
    },
    legend: {
      show: false,
      type: 'scroll',
    },
    animation: true,
    _metric: metric,
    _title: 'metricInfo.displayName',
    _shortMetric: 'metricInfo.short',
    _subscript: 'metricInfo.subscript',
    _unit: 's',
    _viewType: 'timeseries',
  };
};

export const generateTableDataColumn = (
  data: { data: any[]; totals: any[] } = {
    data: [],
    totals: [],
  },
  accountList,
) => {
  const usageMetricsMeta = getUsageMetricsMeta(usageTableMetricList);

  const mergedData = (data.data ?? []).map((aItem) => {
    const match = find(
      accountList,
      (bItem) => String(bItem.customerId) === String(aItem.customer_id),
    );
    return {
      ...aItem,
      customerName: match ? match.customerName : 'Unknown',
    };
  });

  // Get all column keys for columns
  const columnKeys = Object.keys(data?.totals || {});

  // Construct dataSource
  const dataSource = map(
    [{ customerName: TOTAL_BILLABLE_HEADER_DISPLAY_NAME, ...(data?.totals || {}) }, ...mergedData],
    (item) => {
      return mapValues(item, (value, key) => {
        const unit = usageMetricsMeta[key]?.unit;
        if (unit === 'mins') {
          value = value / 60;
        }
        return columnKeys.includes(key) ? humanize(value, usageMetricsMeta[key]?.unit) : value;
      });
    },
  );

  // Construct columns dynamically
  const columns: any[] = [
    {
      title: 'Name',
      key: 'customerName',
      dataKey: 'customerName',
      width: COLUMN_NAME_WIDTH,
      flexShrink: 0,
      align: Column.Alignment.LEFT,
      headerRenderer: CommonHeaderRenderer,
      cellRenderer: CommonCellRenderer,
    },
    ...columnKeys.map((key) => {
      const metric = usageMetricsMeta[key];

      return {
        title: metric?.displayName ?? key,
        key,
        dataKey: key,
        width: COLUMN_DEFAULT_WIDTH,
        align: Column.Alignment.LEFT,
        tooltip: metric?.c3AccountsSumTooltip,
        headerRenderer: CommonHeaderRenderer,
        cellRenderer: CommonCellRenderer,
      };
    }),
  ];

  return {
    dataSource,
    columns,
  };
};
